import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileBarChart,
  History,
  Hotel,
  Key,
  LayoutDashboard,
  Percent,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import dataProvider from '../../authentication/dataProvider';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: number;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
  { label: 'Users', icon: Users, href: '/users' },
  { label: 'Assets', icon: Building2, href: '/assets' },
  { label: 'Rentals', icon: Key, href: '/rental' },
  { label: 'Yields', icon: Percent, href: '/update-yields' },
  { label: 'Amenities', icon: Hotel, href: '/amenities' },
  { label: 'Compliance', icon: ShieldCheck, href: '/compliance' },
  { label: 'Reports', icon: FileBarChart, href: '/reports' },
  { label: 'VESTN Insight AI', icon: Brain, href: '/insight-ai' },
  { label: 'Audit Logs', icon: History, href: '/audit-logs' },
  { label: 'Wallets', icon: Wallet, href: '/wallets' },
];

const bottomNavItems: NavItem[] = [{ label: 'Settings', icon: Settings, href: '/settings' }];

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ isMobileOpen = false, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes (only when menu is open)
  useEffect(() => {
    if (isMobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Only depend on pathname, not isMobileOpen

  // Fetch pending users count for badge
  const { data: usersData } = useQuery({
    queryKey: ['users', 'pending-count'],
    queryFn: async () => {
      try {
        const response = await dataProvider.getList('users', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'DESC' },
          filter: {},
        });
        return response?.data || [];
      } catch (error) {
        return [];
      }
    },
    refetchInterval: 60000, // Refetch every 60 seconds (avoids frequent list calls on every page)
  });

  // Calculate pending users count
  const pendingUsersCount = usersData
    ? usersData.filter(
        (user: { status?: string }) => user.status === 'PENDING' || user.status === 'pending'
      ).length
    : 0;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className='fixed inset-0 bg-black/50 z-40 lg:hidden'
          onClick={onMobileClose}
          aria-hidden='true'
        />
      )}

      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 flex flex-col h-screen bg-sidebar border-r border-sidebar-border transition-transform duration-300 ease-in-out',
          collapsed ? 'w-[72px]' : 'w-[240px] sm:w-[260px]',
          // On mobile/tablet (< lg): show when isMobileOpen is true, hide otherwise
          // On desktop (lg+): always show (lg:translate-x-0 overrides the hidden state)
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        data-mobile-open={isMobileOpen}
      >
        {/* Logo */}
        <div className='flex items-center gap-3 px-4 h-16 border-b border-sidebar-border'>
          <div className='flex items-center justify-center w-10 h-10 rounded-lg bg-primary/20 border border-primary/30 overflow-hidden'>
            <img src='/VESTN_FullLogo.png' alt='VESTN Logo' className='w-8 h-8 object-contain' />
          </div>
          {!collapsed && (
            <div className='flex flex-col animate-fade-in'>
              <span className='font-bold text-foreground tracking-tight'>VESTN</span>
              <span className='text-xs text-muted-foreground'>RWA Admin</span>
            </div>
          )}
          {/* Mobile Close Button */}
          <Button variant='ghost' size='icon' onClick={onMobileClose} className='ml-auto lg:hidden'>
            <X className='w-5 h-5' />
          </Button>
        </div>

        {/* Main Navigation */}
        <nav className='flex-1 overflow-y-auto py-4 px-3'>
          <div className='space-y-1'>
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.href || location.pathname.startsWith(item.href + '/');

              // Get badge count - use dynamic count for Users, static for others
              const badgeCount = item.href === '/users' ? pendingUsersCount : item.badge;

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-primary ml-[-2px]'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                  )}
                >
                  <div className='relative'>
                    <item.icon
                      className={cn(
                        'w-5 h-5 shrink-0 transition-colors',
                        isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'
                      )}
                    />
                    {/* Badge when collapsed - show as small dot or number */}
                    {collapsed && badgeCount && badgeCount > 0 && (
                      <span className='absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground border-2 border-sidebar shadow-sm'>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    <span className='flex-1 text-sm font-medium truncate animate-fade-in'>
                      {item.label}
                    </span>
                  )}
                  {/* Badge when expanded */}
                  {!collapsed && badgeCount && badgeCount > 0 && (
                    <span className='flex items-center justify-center min-w-[22px] h-[22px] px-2 text-xs font-bold rounded-full bg-destructive text-destructive-foreground shadow-sm animate-fade-in'>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Bottom Navigation */}
        <div className='px-3 py-4 border-t border-sidebar-border'>
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                )}
              >
                <item.icon
                  className={cn(
                    'w-5 h-5 shrink-0',
                    isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'
                  )}
                />
                {!collapsed && (
                  <span className='flex-1 text-sm font-medium truncate animate-fade-in'>
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Collapse Toggle - Hidden on mobile */}
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setCollapsed(!collapsed)}
            className='hidden lg:flex w-full mt-2 justify-center text-muted-foreground hover:text-foreground'
          >
            {collapsed ? (
              <ChevronRight className='w-4 h-4' />
            ) : (
              <>
                <ChevronLeft className='w-4 h-4' />
                <span className='animate-fade-in'>Collapse</span>
              </>
            )}
          </Button>
        </div>
      </aside>
    </>
  );
}
